import { MenuItemInfo } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect/lib';
import _ from 'underscore';

import { ButtonControlledFlyoutListMenu, ButtonControlledFlyoutListMenuProps } from '~common/react/components/ButtonControlledFlyoutListMenu';
import { SortableHeader } from '~common/react/components/dataGrid/SortableHeader';
import { CreateExplorerEvent } from '~common/telemetryEvents/TelemetryEventCreator';
import { ExplorerSortEvent, TelemetryEvents } from '~common/telemetryEvents/TelemetryEvents';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { Messages } from '~src/codegen/Localize';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { OrderKeys } from '~src/ts/models/OrderKeys';
import { getDefaultOrderForContentTypesOrSpaceInfo, getOptionsForContentTypes, getPathToOption, getPathToOptionById, OptionDefinition, OptionsDictionary } from '~src/ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '~src/ts/ServerApi/types/IOrderClause';

import { SpaceInfo } from '../../state/SpaceInfo';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';
import { ExplorerDropdown } from './widgets/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from './widgets/ExplorerFlyoutPanel';

export interface SortByDropdownProps {
  readonly order: ReadonlyArray<IOrderClause>;
  readonly orderSelected: (order: ReadonlyArray<IOrderClause>) => void;
  readonly contentTypes: ReadonlyArray<ExplorableItemType>;
  readonly spaceInfo: SpaceInfo;
  readonly hasTextSearchValue: boolean;
  readonly disabled: boolean;
}

export interface SortByDropdownState {
  readonly primarySort: OptionDefinition;
  readonly secondarySort: OptionDefinition;
  readonly options: OptionsDictionary;
  readonly ascending: boolean;
}

export const EMPTY_STATE: SortByDropdownState = {
  primarySort: undefined,
  secondarySort: undefined,
  options: undefined,
  ascending: false
};

export class SortByDropdown extends React.PureComponent<SortByDropdownProps, SortByDropdownState> {

  private memoizedButtonContent = defaultMemoize(getButtonContent);
  private memoizedPanelContent = defaultMemoize(getPanelContent);

  constructor(props: SortByDropdownProps) {
    super(props);
    this.state = mapOrderClauseArrayToState(props.order, props.contentTypes, props.spaceInfo);
  }

  public render(): JSX.Element {
    return (
      <ExplorerDropdown
        disabled={this.props.disabled}
        testId={SortByDropdownTestIds.button}
        renderButtonContent={this.renderButtonContent}
        renderPanel={this.renderPanel}
        panelTitle={Messages.sortByPanelTitle()}
        buttonStyleOverrides={{height: 'auto'}}
      />
    );
  }

  private renderButtonContent = (_showingOrActive: boolean, disabled: boolean): JSX.Element => {
    return this.memoizedButtonContent(this.state, disabled);
  }

  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return this.memoizedPanelContent(this.handleSelection, props, this.state, shouldAddRelevanceToDropdown(this.props));
  }

  public componentWillReceiveProps(nextProps: SortByDropdownProps) {
    if (!_.isEqual(this.props, nextProps)) {
      this.setState(mapOrderClauseArrayToState(nextProps.order, nextProps.contentTypes, nextProps.spaceInfo));
    }
  }

  private handleSelection = (value: string) => {
    const options = memoizedSortOptions(this.props.contentTypes, this.props.spaceInfo);
    const path = getPathToOptionById(value, options);
    let primarySort = path[0];
    let secondarySort = path[1];

    // If primary sort is invalid, default to the first primary sort option available.
    if (!primarySort) {
      primarySort = _.values(options)[0];
    }

    // If secondary sort is not specified, but options exist, default to the first one.
    if (!secondarySort && primarySort.subSortOptions) {
      secondarySort = _.values(primarySort.subSortOptions)[0];
    }

    const state = {
      primarySort,
      secondarySort,
      options,
      ascending: secondarySort ? secondarySort.ascending: primarySort.ascending
    };

    this.setState(state);

    const newSortOrder = mapStateToOrderClauseArray(state);
    this.sendTelemetryEvent(newSortOrder[0]);
    this.props.orderSelected(newSortOrder);
  }

  private sendTelemetryEvent(sort: IOrderClause) {
    const event = CreateExplorerEvent<ExplorerSortEvent>(
      {
        type: TelemetryEvents.ExplorerSort,
        sortField: sort.field,
        ascending: sort.ascending,
        eventSource: 'dropdown'
      });

    Telemetry.sendEvent<ExplorerSortEvent>(event);
  }
}

const getButtonContent = (state: SortByDropdownState, disabled: boolean): JSX.Element => {
  let text = Messages.sortByDropdown_sectionHeader();
  let showArrowIcon = !disabled;
  if (state.secondarySort) {
    text = state.secondarySort.getButtonDisplayValue();
  } else if(state.primarySort) {
    text = state.primarySort.getButtonDisplayValue();
    // Don't draw arrow icon for sorts that don't have sub-sorts (relevance, content type, isCertified)
    showArrowIcon = !disabled  && !_.isUndefined(state.primarySort.subSortOptions);
  }

  return (
    <div>
      {text}
      <SortableHeader.ArrowIcon sorted={showArrowIcon} ascending={state.ascending}/>
    </div>
  );
};

const getPanelContent = (handleSelection: (value: string) => void, flyoutProps: ExplorerFlyoutPanelProps, state: SortByDropdownState, shouldAddRelevanceToDropdown: boolean): JSX.Element => {
  const menuItems = buildMenuItems(state, state.options, shouldAddRelevanceToDropdown);
  const panelProps: ButtonControlledFlyoutListMenuProps = {
    onComponentDidMount: _.noop,
    menuItems: menuItems,
    onMenuItemSelected: handleSelection,
    anchorElement: flyoutProps.anchorEl,
    closeMe: flyoutProps.onCloseRequest,
    testId: SortByDropdownTestIds.childPanelWrapper,
    id: `${flyoutProps.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`,
    role: 'menu'
  };
  return <ButtonControlledFlyoutListMenu {...panelProps} />;
};

const memoizedSortOptions = defaultMemoize(getSortOptionsByContentTypes);

function getSortOptionsByContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): OptionsDictionary {
  const result = getOptionsForContentTypes(contentTypes, spaceInfo);
  return result;
}

function mapStateToOrderClauseArray(state: SortByDropdownState): ReadonlyArray<IOrderClause> {
  return state.secondarySort ? state.secondarySort.toOrderClauseArray() : state.primarySort.toOrderClauseArray();
}

function mapOrderClauseArrayToState(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): SortByDropdownState {
  const definitions = memoizedSortOptions(contentTypes, spaceInfo);

  if (_.values(definitions).length === 0) {
    return EMPTY_STATE;
  }

  if (order && order.length > 0) {
    const sortOptions = getPathToOption(order[0], definitions);
    if (sortOptions.length > 0) {
      const primarySort = sortOptions[0];
      const secondarySort = sortOptions[1];
      const displaySecondarySort = secondarySortIsChildOfPrimarySort(primarySort, secondarySort);
      return {
        primarySort: sortOptions[0],
        secondarySort: displaySecondarySort ? sortOptions[1] : undefined,
        options: definitions,
        ascending: order[0].ascending
      };
    }
  }

  const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
  if (!defaultOrder || defaultOrder.length === 0){
    // This causes infinite recursion when no defaultOrder is defined/found. This should not happen.
    throw new Error('No default sort order could be found');
  }
  return mapOrderClauseArrayToState(defaultOrder, contentTypes, spaceInfo);
}

function secondarySortIsChildOfPrimarySort(primarySort: OptionDefinition, secondarySort: OptionDefinition): boolean {
  return secondarySort &&
         primarySort.subSortOptions &&
         _.values(primarySort.subSortOptions).some(child => child.id === secondarySort.id);
}

function buildMenuItems(state: SortByDropdownState, definitions: OptionsDictionary, shouldAddRelevanceToDropdown: boolean): MenuItemInfo[] {
  const items: MenuItemInfo[] = [];

  if (state.primarySort && state.primarySort.subSortOptions) {
    items.push({
      value: 'orderSectionHeader',
      displayValue: Messages.sortByOrder(),
      disabled: true,
      checked: false
    });

    for(const subSortDefinition of _.values(state.primarySort.subSortOptions)) {
      items.push({
        value: subSortDefinition.id,
        displayValue: subSortDefinition.getMenuItemDisplayValue(),
        disabled: false,
        checked: state.secondarySort && state.secondarySort.id === subSortDefinition.id
      });
    }

    items.push({
      value: 'sectionGap',
      displayValue: ' ',
      disabled: true,
      checked: false
    });
  }

  items.push({
    value: 'sortBySectionHeader',
    displayValue: Messages.sortByDropdown_sectionHeader(),
    disabled: true,
    checked: false
  });


  for(const sortDefinition of _.values(definitions)) {
    if (sortDefinition.id === OrderKeys.relevance && !shouldAddRelevanceToDropdown) {
      continue;
    }

    items.push({
      value: sortDefinition.id,
      displayValue: sortDefinition.getMenuItemDisplayValue(),
      disabled: false,
      checked: sortDefinition.id === state.primarySort.id
    });
  }

  return items;
}

export namespace SortByDropdownTestIds {
  export const dropDown = 'content-explorer-sort-by-dropdown';
  export const button = 'content-explorer-sort-by-button';
  export const childPanelWrapper = 'content-explorer-sort-by-panel-wrapper';
}

function shouldAddRelevanceToDropdown(props: SortByDropdownProps) {
  return props.hasTextSearchValue;
}
